import React, { useState, useEffect, useCallback } from 'react';
import { configApi, academicApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MultiSelect } from '../components/MultiSelect';
import { Loader2, Settings2, GraduationCap, AlertCircle, Mail, DollarSign, Clock, Send, Save, Building2, Search, MessageCircle, Copy, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BRANCHES = ['KISAASI', 'BUNGA', 'KIGALI', 'LUBOWA'];

const SettingsPage = () => {
  const { hasPageAccess } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [promoting, setPromoting] = useState(false);
  const [promotionData, setPromotionData] = useState({
    from_class: '',
    to_class: '',
    academic_year: '2025-2026',
    branch: ''
  });

  // Academic promote student list
  const [promotionStudents, setPromotionStudents] = useState([]);
  const [promotionStudentsLoading, setPromotionStudentsLoading] = useState(false);
  const [promotionSelectedIds, setPromotionSelectedIds] = useState(new Set());
  const [promotionSearch, setPromotionSearch] = useState('');

  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Acorns School',
    email_body_text: ''
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [invoiceEmailBranch, setInvoiceEmailBranch] = useState('ALL'); // 'ALL' or a branch code

  // Custom bulk email composer (branch-wise + term-wise, both multi-select)
  const [customEmail, setCustomEmail] = useState({ subject: '', message: '' });
  const [customBranches, setCustomBranches] = useState([]); // empty = all branches
  const [customTerms, setCustomTerms] = useState([]);       // empty = all terms; values are "ay|term" keys
  const [termOptions, setTermOptions] = useState([]);       // [{key,label,...}]

  // WhatsApp Settings
  const [whatsappSettings, setWhatsappSettings] = useState({
    phone_number_id: '',
    access_token: '',
    business_account_id: '',
    language_code: 'en',
    is_enabled: false
  });
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [testPhone, setTestPhone] = useState('');

  // Exchange Rates
  const [exchangeRates, setExchangeRates] = useState({
    ugx_to_usd: 0.000267,
    rwf_to_usd: 0.00077
  });

  // Late-fee surcharge
  const [surchargePct, setSurchargePct] = useState(5);

  // Tally Schedule
  const [tallySchedule, setTallySchedule] = useState({
    tally_a_enabled: false,
    tally_a_time: '00:00',
    tally_b_enabled: false,
    tally_b_time: '00:00'
  });

  // Branch Settings
  const [selectedBranch, setSelectedBranch] = useState('KISAASI');
  const [branchSettings, setBranchSettings] = useState({});
  const [currentBranchData, setCurrentBranchData] = useState({
    branch: 'KISAASI',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    extra_notes: '',
    receipt_email_text: '',
    email_signature: ''
  });

  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, smtpRes, ratesRes, scheduleRes, branchesRes, whatsappRes, templatesRes, surchargeRes] = await Promise.all([
        configApi.get(),
        api.get('/settings/smtp').catch(() => ({ data: {} })),
        api.get('/settings/exchange-rates').catch(() => ({ data: {} })),
        api.get('/settings/tally-schedule').catch(() => ({ data: {} })),
        api.get('/settings/branches').catch(() => ({ data: {} })),
        api.get('/settings/whatsapp').catch(() => ({ data: {} })),
        api.get('/settings/whatsapp/templates').catch(() => ({ data: { templates: [] } })),
        api.get('/settings/surcharge').catch(() => ({ data: { percentage: 5 } }))
      ]);

      api.get('/invoices/term-options')
        .then((res) => setTermOptions(res.data?.options || []))
        .catch(() => setTermOptions([]));
      
      setConfig(configRes.data);
      
      if (smtpRes.data.is_configured) {
        setSmtpSettings({ email_body_text: '', ...smtpRes.data });
        setSmtpConfigured(true);
      }
      
      if (whatsappRes.data.phone_number_id) {
        setWhatsappSettings(whatsappRes.data);
        setWhatsappConfigured(whatsappRes.data.configured);
      }
      
      if (templatesRes.data.templates) {
        setWhatsappTemplates(templatesRes.data.templates);
      }
      
      if (ratesRes.data.ugx_to_usd) {
        setExchangeRates({
          ugx_to_usd: ratesRes.data.ugx_to_usd,
          rwf_to_usd: ratesRes.data.rwf_to_usd
        });
      }
      
      if (scheduleRes.data) {
        setTallySchedule(scheduleRes.data);
      }

      if (branchesRes.data) {
        setBranchSettings(branchesRes.data);
        if (branchesRes.data['KISAASI']) {
          setCurrentBranchData(branchesRes.data['KISAASI']);
        }
      }

      if (surchargeRes.data && surchargeRes.data.percentage != null) {
        setSurchargePct(parseFloat(surchargeRes.data.percentage));
      }
    } catch (err) {
      toast.error('Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (branchSettings[selectedBranch]) {
      setCurrentBranchData(branchSettings[selectedBranch]);
    } else {
      setCurrentBranchData({
        branch: selectedBranch,
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        extra_notes: '',
        receipt_email_text: '',
        email_signature: ''
      });
    }
  }, [selectedBranch, branchSettings]);

  const fetchPromotionStudents = async (fromClass, branch) => {
    if (!fromClass) {
      setPromotionStudents([]);
      return;
    }
    setPromotionStudentsLoading(true);
    try {
      const params = { from_class: fromClass };
      if (branch) params.branch = branch;
      const res = await academicApi.getStudentsForPromotion(params);
      setPromotionStudents(res.data || []);
      // Default to NO students selected — staff explicitly pick who to promote
      // (cc.xlsx June-11: "by default no single student selected").
      setPromotionSelectedIds(new Set());
    } catch (err) {
      toast.error('Failed to load students');
      console.error(err);
    } finally {
      setPromotionStudentsLoading(false);
    }
  };

  const handlePromotion = async () => {
    if (!promotionData.from_class || !promotionData.to_class) {
      toast.error('Please select both classes');
      return;
    }
    if (promotionSelectedIds.size === 0) {
      toast.error('Please select at least one student');
      return;
    }
    if (!window.confirm(`Promote ${promotionSelectedIds.size} students from ${promotionData.from_class} to ${promotionData.to_class}?`)) return;
    
    setPromoting(true);
    try {
      const response = await academicApi.promote({
        from_class: promotionData.from_class,
        to_class: promotionData.to_class,
        academic_year: promotionData.academic_year,
        student_ids: Array.from(promotionSelectedIds)
      });
      toast.success(response.data.message);
      setPromotionData({ ...promotionData, from_class: '', to_class: '' });
      setPromotionStudents([]);
      setPromotionSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote students');
    } finally {
      setPromoting(false);
    }
  };

  const togglePromotionStudent = (id) => {
    setPromotionSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePromotionSelectAll = () => {
    const filtered = filteredPromotionStudents;
    if (filtered.every(s => promotionSelectedIds.has(s.id))) {
      const next = new Set(promotionSelectedIds);
      filtered.forEach(s => next.delete(s.id));
      setPromotionSelectedIds(next);
    } else {
      const next = new Set(promotionSelectedIds);
      filtered.forEach(s => next.add(s.id));
      setPromotionSelectedIds(next);
    }
  };

  const filteredPromotionStudents = promotionStudents.filter(s => {
    if (!promotionSearch) return true;
    const q = promotionSearch.toLowerCase();
    return (
      (s.first_name || '').toLowerCase().includes(q) ||
      (s.last_name || '').toLowerCase().includes(q) ||
      (s.student_id || '').toLowerCase().includes(q)
    );
  });

  const saveSMTPSettings = async () => {
    setSaving({ ...saving, smtp: true });
    try {
      await api.post('/settings/smtp', smtpSettings);
      toast.success('SMTP settings saved');
      setSmtpConfigured(true);
    } catch (err) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving({ ...saving, smtp: false });
    }
  };

  const testSMTPSettings = async () => {
    setSaving({ ...saving, smtpTest: true });
    try {
      const response = await api.post('/settings/smtp/test');
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error('SMTP test failed');
    } finally {
      setSaving({ ...saving, smtpTest: false });
    }
  };

  const sendAllInvoiceEmails = async () => {
    const branch = invoiceEmailBranch === 'ALL' ? '' : invoiceEmailBranch;
    const scopeLabel = branch ? `the ${branch} branch` : 'ALL branches';
    if (!window.confirm(`Send invoice emails for ${scopeLabel} to parents? The term is taken from ${branch ? "this branch's" : "the"} most recently generated invoice, and only that term's invoices are sent (one email per invoice); older terms are not emailed. This cannot be undone.`)) return;
    setSaving({ ...saving, sendInvoices: true });
    try {
      const response = await api.post('/invoices/send-all-emails', null, { params: branch ? { branch } : {} });
      toast.success(response.data.message || `Started sending ${response.data.total} invoice emails`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice emails');
    } finally {
      setSaving({ ...saving, sendInvoices: false });
    }
  };

  const sendCustomEmail = async () => {
    if (!customEmail.subject.trim() || !customEmail.message.trim()) {
      toast.error('Subject and message are both required');
      return;
    }
    const branchLabel = customBranches.length ? customBranches.join(', ') : 'ALL branches';
    const termLabel = customTerms.length
      ? customTerms.map((k) => termOptions.find((o) => o.key === k)?.label || k).join(', ')
      : 'ALL terms';
    if (!window.confirm(`Send this email to parents of students in: ${branchLabel} / ${termLabel}? One email per student. This cannot be undone.`)) return;
    setSaving({ ...saving, customEmail: true });
    try {
      const response = await api.post('/communications/send-custom-email', {
        subject: customEmail.subject,
        message: customEmail.message,
        branches: customBranches,
        terms: customTerms,
      });
      toast.success(response.data.message || `Started sending to ${response.data.total} students`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSaving({ ...saving, customEmail: false });
    }
  };

  const saveExchangeRates = async () => {
    setSaving({ ...saving, rates: true });
    try {
      await api.post('/settings/exchange-rates', exchangeRates);
      toast.success('Exchange rates saved');
    } catch (err) {
      toast.error('Failed to save exchange rates');
    } finally {
      setSaving({ ...saving, rates: false });
    }
  };

  const saveSurcharge = async () => {
    setSaving({ ...saving, surcharge: true });
    try {
      const pct = parseFloat(surchargePct);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        toast.error('Surcharge % must be between 0 and 100');
        return;
      }
      await api.post('/settings/surcharge', { percentage: pct });
      toast.success(`Surcharge set to ${pct}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save surcharge');
    } finally {
      setSaving({ ...saving, surcharge: false });
    }
  };

  const saveTallySchedule = async () => {
    setSaving({ ...saving, tally: true });
    try {
      await api.post('/settings/tally-schedule', tallySchedule);
      toast.success('Tally schedule saved');
    } catch (err) {
      toast.error('Failed to save Tally schedule');
    } finally {
      setSaving({ ...saving, tally: false });
    }
  };

  const saveBranchSettings = async () => {
    setSaving({ ...saving, branch: true });
    try {
      await api.post('/settings/branches', currentBranchData);
      toast.success(`${selectedBranch} branch settings saved`);
      setBranchSettings({ ...branchSettings, [selectedBranch]: currentBranchData });
    } catch (err) {
      toast.error('Failed to save branch settings');
    } finally {
      setSaving({ ...saving, branch: false });
    }
  };

  if (!hasPageAccess('settings')) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-[#C85A40] mb-4" />
        <p className="text-[#5C635F]">You don't have permission to access Settings</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Settings</h1>
        <p className="text-[#5C635F] mt-1">System configuration and integrations</p>
      </div>

      <Tabs defaultValue="branches" className="space-y-4">
        <TabsList className="bg-[#ECEDF5] flex-wrap">
          <TabsTrigger value="branches" data-testid="tab-branches">
            <Building2 className="w-4 h-4 mr-2" />
            Branches
          </TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general">
            <Settings2 className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="exchange" data-testid="tab-exchange">
            <DollarSign className="w-4 h-4 mr-2" />
            Exchange
          </TabsTrigger>
          <TabsTrigger value="tally-schedule" data-testid="tab-tally-schedule">
            <Clock className="w-4 h-4 mr-2" />
            Tally
          </TabsTrigger>
          <TabsTrigger value="academic" data-testid="tab-academic">
            <GraduationCap className="w-4 h-4 mr-2" />
            Academic
          </TabsTrigger>
        </TabsList>

        {/* Branch Settings Tab */}
        <TabsContent value="branches">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#3E3F96]" />
                Branch Settings
              </CardTitle>
              <CardDescription>
                Configure branch-specific details for invoices and receipts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Branch</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger data-testid="branch-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BRANCHES.map((branch) => (
                      <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={currentBranchData.address}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, address: e.target.value })}
                    placeholder="Street address"
                    data-testid="branch-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={currentBranchData.city}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, city: e.target.value })}
                    placeholder="City"
                    data-testid="branch-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={currentBranchData.country}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, country: e.target.value })}
                    placeholder="Country"
                    data-testid="branch-country"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={currentBranchData.phone}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, phone: e.target.value })}
                    placeholder="+256 700 000000"
                    data-testid="branch-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={currentBranchData.email}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, email: e.target.value })}
                    placeholder="branch@acorns.edu"
                    data-testid="branch-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={currentBranchData.website}
                    onChange={(e) => setCurrentBranchData({ ...currentBranchData, website: e.target.value })}
                    placeholder="www.acornsschools.com"
                    data-testid="branch-website"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Extra Notes (printed on invoices)</Label>
                <Textarea
                  value={currentBranchData.extra_notes}
                  onChange={(e) => setCurrentBranchData({ ...currentBranchData, extra_notes: e.target.value })}
                  placeholder="E.g., Bank details, payment instructions..."
                  rows={3}
                  data-testid="branch-notes"
                />
              </div>

              <div className="space-y-2">
                <Label>Receipt Email Text ({selectedBranch}-specific, added to receipt emails)</Label>
                <Textarea
                  value={currentBranchData.receipt_email_text || ''}
                  onChange={(e) => setCurrentBranchData({ ...currentBranchData, receipt_email_text: e.target.value })}
                  placeholder={`Branch-specific text added to the body of receipt emails for ${selectedBranch}. Leave blank for none.`}
                  rows={4}
                  data-testid="branch-receipt-email-text"
                />
              </div>

              <div className="space-y-2">
                <Label>Email Signature ({selectedBranch}-specific, added at the end of emails)</Label>
                <Textarea
                  value={currentBranchData.email_signature || ''}
                  onChange={(e) => setCurrentBranchData({ ...currentBranchData, email_signature: e.target.value })}
                  placeholder={`E.g.,\nRegards,\n${selectedBranch} Office\nAcorns International Schools`}
                  rows={4}
                  data-testid="branch-email-signature"
                />
              </div>

              <div className="pt-4">
                <Button
                  onClick={saveBranchSettings}
                  className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  disabled={saving.branch}
                  data-testid="save-branch-btn"
                >
                  {saving.branch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save {selectedBranch} Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">System Configuration</CardTitle>
              <CardDescription>Read-only system settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-[#F5F5FA] rounded-lg">
                  <p className="text-xs text-[#5C635F] uppercase">Max Users</p>
                  <p className="text-lg font-bold text-[#1C1F1D]">{config?.max_users}</p>
                </div>
                <div className="p-3 bg-[#F5F5FA] rounded-lg">
                  <p className="text-xs text-[#5C635F] uppercase">License Expiry</p>
                  <p className="text-lg font-bold text-[#1C1F1D]">{config?.license_expiry}</p>
                </div>
                <div className="p-3 bg-[#F5F5FA] rounded-lg">
                  <p className="text-xs text-[#5C635F] uppercase">Base Currency</p>
                  <p className="text-lg font-bold text-[#1C1F1D]">{config?.default_currency}</p>
                </div>
                <div className="p-3 bg-[#F5F5FA] rounded-lg">
                  <p className="text-xs text-[#5C635F] uppercase">Withholding Tax</p>
                  <p className="text-lg font-bold text-[#1C1F1D]">{(config?.withholding_tax_rate || 0) * 100}%</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-[#5C635F] mb-2">Branches</p>
                <div className="flex gap-2 flex-wrap">
                  {BRANCHES.map((branch) => (
                    <Badge key={branch} className="bg-[#3E3F96]/10 text-[#3E3F96]">{branch}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-[#5C635F] mb-2">Fee Head Priority</p>
                <div className="flex flex-wrap gap-2">
                  {config?.fee_head_priority && Object.entries(config.fee_head_priority)
                    .sort(([, a], [, b]) => a - b)
                    .map(([head, priority]) => (
                      <Badge key={head} className="bg-[#ECEDF5] text-[#3E3F96] capitalize">
                        {priority}. {head}
                      </Badge>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#3E3F96]" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>
                Configure email settings for payment receipts and notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {smtpConfigured && (
                <div className="p-3 bg-[#5556A8]/10 border border-[#5556A8]/20 rounded-lg">
                  <p className="text-sm text-[#5556A8] font-medium">SMTP is configured</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host</Label>
                  <Input
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    value={smtpSettings.smtp_username}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_username: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={smtpSettings.smtp_password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                    placeholder="App password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input
                    value={smtpSettings.smtp_from_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_email: e.target.value })}
                    placeholder="noreply@school.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={smtpSettings.smtp_from_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_name: e.target.value })}
                    placeholder="Acorns School"
                  />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Label>Email Body Text</Label>
                <Textarea
                  value={smtpSettings.email_body_text}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, email_body_text: e.target.value })}
                  placeholder="This text is added to the body of every email sent (invoices, receipts, notifications)."
                  rows={4}
                />
                <p className="text-xs text-[#5C635F]">Applied to the body of all emails. Leave blank for none.</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={saveSMTPSettings} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving.smtp}>
                  {saving.smtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
                <Button variant="outline" onClick={testSMTPSettings} disabled={saving.smtpTest || !smtpConfigured}>
                  {saving.smtpTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Email
                </Button>
              </div>

              <div className="pt-4 mt-2 border-t border-[#D8D9E8]">
                <Label className="text-sm font-semibold text-[#3E3F96]">Send Invoice Emails</Label>
                <p className="text-xs text-[#5C635F] mt-1 mb-3">
                  Emails invoices for the <strong>latest term</strong> — taken from the selected branch's most recently generated invoice — to students' parents (one email per invoice). Older / historical terms are not sent. Choose a branch to send only that branch's invoices, or "All Branches". Parents without an email on file are skipped. Runs in the background — results are written to the audit log.
                </p>
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#5C635F]">Branch</Label>
                    <Select value={invoiceEmailBranch} onValueChange={setInvoiceEmailBranch}>
                      <SelectTrigger className="w-48" data-testid="invoice-email-branch">
                        <SelectValue placeholder="All Branches" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Branches</SelectItem>
                        {BRANCHES.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={sendAllInvoiceEmails} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving.sendInvoices || !smtpConfigured}>
                    {saving.sendInvoices ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {invoiceEmailBranch === 'ALL' ? 'Send All Invoice Emails' : `Send ${invoiceEmailBranch} Invoice Emails`}
                  </Button>
                </div>
                {!smtpConfigured && (
                  <p className="text-xs text-amber-600 mt-2">Save SMTP settings first to enable sending.</p>
                )}
              </div>

              <div className="pt-4 mt-2 border-t border-[#D8D9E8]">
                <Label className="text-sm font-semibold text-[#3E3F96]">Send Custom Email</Label>
                <p className="text-xs text-[#5C635F] mt-1 mb-3">
                  Compose a custom subject and message and send it to students' parents — filtered by <strong>branch</strong> and <strong>term</strong> (both multi-select). One email per student. Leave a filter empty to mean "all". When terms are selected, only students with an invoice in those terms receive the email. Parents without an email on file are skipped. Runs in the background.
                </p>
                <div className="flex items-end gap-3 flex-wrap mb-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#5C635F]">Branches</Label>
                    <MultiSelect
                      options={BRANCHES}
                      selected={customBranches}
                      onChange={setCustomBranches}
                      placeholder="All Branches"
                      testId="custom-email-branches"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#5C635F]">Terms</Label>
                    <MultiSelect
                      options={termOptions.map((o) => ({ value: o.key, label: o.label }))}
                      selected={customTerms}
                      onChange={setCustomTerms}
                      placeholder="All Terms"
                      widthClass="w-56"
                      testId="custom-email-terms"
                    />
                  </div>
                </div>
                <div className="space-y-2 max-w-3xl">
                  <div>
                    <Label className="text-xs text-[#5C635F]">Subject</Label>
                    <Input
                      value={customEmail.subject}
                      onChange={(e) => setCustomEmail({ ...customEmail, subject: e.target.value })}
                      placeholder="e.g. Term 1 reopening date"
                      data-testid="custom-email-subject"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5C635F]">Message</Label>
                    <Textarea
                      rows={6}
                      value={customEmail.message}
                      onChange={(e) => setCustomEmail({ ...customEmail, message: e.target.value })}
                      placeholder="Type the message parents will receive. Line breaks are preserved."
                      data-testid="custom-email-message"
                    />
                  </div>
                </div>
                <Button
                  onClick={sendCustomEmail}
                  className="bg-[#3E3F96] hover:bg-[#2D2E7A] mt-3"
                  disabled={saving.customEmail || !smtpConfigured}
                >
                  {saving.customEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Custom Email
                </Button>
                {!smtpConfigured && (
                  <p className="text-xs text-amber-600 mt-2">Save SMTP settings first to enable sending.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings Tab */}
        <TabsContent value="whatsapp">
          <div className="space-y-6">
            <Card className="border-[#D8D9E8]">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  WhatsApp Business API Configuration
                </CardTitle>
                <CardDescription>
                  Configure Meta WhatsApp Business Cloud API for sending notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#F5F5FA] rounded-lg">
                  <p className="text-sm text-[#5C635F]">
                    To use WhatsApp notifications, you need a Meta Business account with WhatsApp Business API access.
                    <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="text-[#3E3F96] ml-1 hover:underline">
                      Setup Guide →
                    </a>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number ID</Label>
                    <Input
                      placeholder="e.g., 123456789012345"
                      value={whatsappSettings.phone_number_id}
                      onChange={(e) => setWhatsappSettings({ ...whatsappSettings, phone_number_id: e.target.value })}
                      data-testid="whatsapp-phone-id"
                    />
                    <p className="text-xs text-[#5C635F]">From Meta Business Dashboard</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Business Account ID (Optional)</Label>
                    <Input
                      placeholder="e.g., 987654321098765"
                      value={whatsappSettings.business_account_id}
                      onChange={(e) => setWhatsappSettings({ ...whatsappSettings, business_account_id: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Access Token</Label>
                  <Input
                    type="password"
                    placeholder="Permanent Access Token from Meta"
                    value={whatsappSettings.access_token}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, access_token: e.target.value })}
                    data-testid="whatsapp-access-token"
                  />
                  <p className="text-xs text-[#5C635F]">Generate a permanent token from System Users in Meta Business Settings</p>
                </div>

                <div className="flex items-center justify-between p-4 bg-[#F5F5FA] rounded-lg">
                  <div>
                    <p className="font-medium text-[#1C1F1D]">Enable WhatsApp Notifications</p>
                    <p className="text-sm text-[#5C635F]">Send invoice, payment, credit note notifications via WhatsApp</p>
                  </div>
                  <Switch
                    checked={whatsappSettings.is_enabled}
                    onCheckedChange={(v) => setWhatsappSettings({ ...whatsappSettings, is_enabled: v })}
                    data-testid="whatsapp-enabled-switch"
                  />
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <Button
                    className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                    onClick={async () => {
                      setSaving({ ...saving, whatsapp: true });
                      try {
                        await api.post('/settings/whatsapp', whatsappSettings);
                        toast.success('WhatsApp settings saved');
                        setWhatsappConfigured(true);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to save WhatsApp settings');
                      } finally {
                        setSaving({ ...saving, whatsapp: false });
                      }
                    }}
                    disabled={saving.whatsapp}
                    data-testid="save-whatsapp-btn"
                  >
                    {saving.whatsapp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Settings
                  </Button>
                </div>

                {/* Test WhatsApp */}
                {whatsappConfigured && (
                  <div className="pt-4 border-t">
                    <Label className="mb-2 block">Test WhatsApp Connection</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Phone number (e.g., +256701234567)"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        className="max-w-xs"
                      />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          if (!testPhone) {
                            toast.error('Enter a phone number');
                            return;
                          }
                          setSaving({ ...saving, whatsappTest: true });
                          try {
                            const res = await api.post('/settings/whatsapp/test', { phone_number: testPhone });
                            if (res.data.success) {
                              toast.success(res.data.message);
                            } else {
                              toast.error(res.data.message);
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Test failed');
                          } finally {
                            setSaving({ ...saving, whatsappTest: false });
                          }
                        }}
                        disabled={saving.whatsappTest}
                      >
                        {saving.whatsappTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Test
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* WhatsApp Templates */}
            <Card className="border-[#D8D9E8]">
              <CardHeader>
                <CardTitle className="text-lg">Message Templates for Approval</CardTitle>
                <CardDescription>
                  Submit these templates to Meta for approval. Copy and use in your Meta Business Manager.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {whatsappTemplates.map((template, idx) => (
                    <div key={idx} className="p-4 border border-[#D8D9E8] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#25D366]/10 text-[#25D366]">{template.name}</Badge>
                          <Badge variant="outline">{template.category}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(template.body);
                            toast.success('Template copied to clipboard');
                          }}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <div className="bg-[#F5F5FA] p-3 rounded-lg text-sm font-mono whitespace-pre-wrap">
                        {template.body}
                      </div>
                      <div className="mt-2 text-xs text-[#5C635F]">
                        <strong>Variables:</strong> {template.variables.map((v, i) => `{{${i+1}}} = ${v}`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Exchange Rates Tab */}
        <TabsContent value="exchange">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#3E3F96]" />
                Exchange Rates
              </CardTitle>
              <CardDescription>
                Set conversion rates to USD (base currency)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-[#F5F5FA] rounded-lg">
                <p className="text-sm text-[#5C635F]">All dashboard amounts will be displayed in USD using these rates</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>1 UGX = USD</Label>
                  <Input
                    type="number"
                    step="0.0000001"
                    value={exchangeRates.ugx_to_usd}
                    onChange={(e) => setExchangeRates({ ...exchangeRates, ugx_to_usd: parseFloat(e.target.value) })}
                  />
                  <p className="text-xs text-[#5C635F]">Example: 0.000267 means 1 USD = 3,745 UGX</p>
                </div>
                <div className="space-y-2">
                  <Label>1 RWF = USD</Label>
                  <Input
                    type="number"
                    step="0.0000001"
                    value={exchangeRates.rwf_to_usd}
                    onChange={(e) => setExchangeRates({ ...exchangeRates, rwf_to_usd: parseFloat(e.target.value) })}
                  />
                  <p className="text-xs text-[#5C635F]">Example: 0.00077 means 1 USD = 1,299 RWF</p>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={saveExchangeRates} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving.rates}>
                  {saving.rates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Exchange Rates
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#D8D9E8] mt-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#C85A40]" />
                Late-Fee Surcharge
              </CardTitle>
              <CardDescription>
                Auto-applied to invoices whose due date has passed and outstanding balance is non-zero. Calculated as a percentage of the original invoice total.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Surcharge Percentage</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={surchargePct}
                      onChange={(e) => setSurchargePct(e.target.value)}
                      className="max-w-[200px]"
                    />
                    <span className="text-sm text-[#5C635F]">%</span>
                  </div>
                  <p className="text-xs text-[#5C635F]">Default is 5%. Changes apply instantly to all overdue invoices.</p>
                </div>
              </div>
              <div className="pt-4">
                <Button onClick={saveSurcharge} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving.surcharge}>
                  {saving.surcharge ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Surcharge
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tally Schedule Tab */}
        <TabsContent value="tally-schedule">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#3E3F96]" />
                Tally Auto-Post Schedule
              </CardTitle>
              <CardDescription>
                Configure automatic posting of transactions to Tally A and Tally B separately
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tally A Schedule */}
              <div className="p-4 border border-[#D8D9E8] rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#3E3F96]">Tally A (All Transactions)</p>
                    <p className="text-sm text-[#5C635F]">Posts all payments and expenses</p>
                  </div>
                  <Switch
                    checked={tallySchedule.tally_a_enabled}
                    onCheckedChange={(checked) => setTallySchedule({ ...tallySchedule, tally_a_enabled: checked })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Schedule Time (24h)</Label>
                  <Input
                    type="time"
                    value={tallySchedule.tally_a_time}
                    onChange={(e) => setTallySchedule({ ...tallySchedule, tally_a_time: e.target.value })}
                    disabled={!tallySchedule.tally_a_enabled}
                    className="max-w-[200px]"
                  />
                </div>
              </div>

              {/* Tally B Schedule */}
              <div className="p-4 border border-[#D8D9E8] rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#5556A8]">Tally B (Bank Only)</p>
                    <p className="text-sm text-[#5C635F]">Posts only bank deposit payments</p>
                  </div>
                  <Switch
                    checked={tallySchedule.tally_b_enabled}
                    onCheckedChange={(checked) => setTallySchedule({ ...tallySchedule, tally_b_enabled: checked })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Schedule Time (24h)</Label>
                  <Input
                    type="time"
                    value={tallySchedule.tally_b_time}
                    onChange={(e) => setTallySchedule({ ...tallySchedule, tally_b_time: e.target.value })}
                    disabled={!tallySchedule.tally_b_enabled}
                    className="max-w-[200px]"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={saveTallySchedule} className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving.tally}>
                  {saving.tally ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Academic Tab */}
        <TabsContent value="academic">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#3E3F96]" />
                Batch Promotion
              </CardTitle>
              <CardDescription>Select students to promote from one class to another</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#D99C3B]/10 border border-[#D99C3B]/20 text-[#D99C3B] px-4 py-3 rounded-lg text-sm">
                <p className="font-medium">Warning</p>
                <p>Select students below and promote them. This action cannot be undone.</p>
              </div>

              <div className="space-y-2">
                <Label>Academic Year</Label>
                <Select 
                  value={promotionData.academic_year} 
                  onValueChange={(v) => setPromotionData({ ...promotionData, academic_year: v })}
                >
                  <SelectTrigger data-testid="promo-academic-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Class</Label>
                  <Select 
                    value={promotionData.from_class} 
                    onValueChange={(v) => { 
                      setPromotionData({ ...promotionData, from_class: v });
                      fetchPromotionStudents(v, promotionData.branch);
                    }}
                  >
                    <SelectTrigger data-testid="promo-from-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {config?.classes?.map((cls) => (
                        <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Class</Label>
                  <Select 
                    value={promotionData.to_class} 
                    onValueChange={(v) => setPromotionData({ ...promotionData, to_class: v })}
                  >
                    <SelectTrigger data-testid="promo-to-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {config?.classes?.map((cls) => (
                        <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                      ))}
                      <SelectItem value="Qualified" className="font-semibold text-emerald-700">Qualified (crossed top class)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {promotionData.to_class === 'Qualified' && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                  Selected students will be marked <span className="font-medium">Qualified</span> and moved to the Qualified tab on the Students page.
                </div>
              )}

              <div className="space-y-2">
                <Label>Branch (Optional - filter students)</Label>
                <Select 
                  value={promotionData.branch || "all_branches"} 
                  onValueChange={(v) => { 
                    const branch = v === "all_branches" ? "" : v;
                    setPromotionData({ ...promotionData, branch });
                    if (promotionData.from_class) {
                      fetchPromotionStudents(promotionData.from_class, branch);
                    }
                  }}
                >
                  <SelectTrigger data-testid="promo-branch"><SelectValue placeholder="All Branches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_branches">All Branches</SelectItem>
                    {config?.branches?.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Student List */}
              {promotionData.from_class && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">
                      Students in {promotionData.from_class}
                      {promotionStudents.length > 0 && (
                        <span className="text-sm font-normal text-[#5C635F] ml-2">
                          ({promotionSelectedIds.size}/{promotionStudents.length} selected)
                        </span>
                      )}
                    </Label>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input
                      placeholder="Search students..."
                      value={promotionSearch}
                      onChange={(e) => setPromotionSearch(e.target.value)}
                      className="pl-9"
                      data-testid="promo-student-search"
                    />
                  </div>

                  {promotionStudentsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
                    </div>
                  ) : promotionStudents.length === 0 ? (
                    <div className="p-6 text-center text-[#5C635F] bg-[#F5F5FA] rounded-lg">
                      No students found in {promotionData.from_class}
                    </div>
                  ) : (
                    <div className="border border-[#D8D9E8] rounded-lg max-h-80 overflow-y-auto">
                      {/* Select All header */}
                      <div className="flex items-center gap-3 px-4 py-2 bg-[#F5F5FA] border-b border-[#D8D9E8] sticky top-0">
                        <Checkbox
                          checked={filteredPromotionStudents.length > 0 && filteredPromotionStudents.every(s => promotionSelectedIds.has(s.id))}
                          onCheckedChange={togglePromotionSelectAll}
                          data-testid="promo-select-all"
                        />
                        <span className="text-xs uppercase font-semibold text-[#5C635F]">Select All</span>
                      </div>
                      {filteredPromotionStudents.map((student) => (
                        <div
                          key={student.id}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-[#F5F5FA] border-b border-[#D8D9E8] last:border-0"
                          data-testid={`promo-student-${student.student_id}`}
                        >
                          <Checkbox
                            checked={promotionSelectedIds.has(student.id)}
                            onCheckedChange={() => togglePromotionStudent(student.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{student.first_name} {student.last_name}</p>
                            <p className="text-xs text-[#5C635F]">{student.student_id} - {student.branch}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button 
                onClick={handlePromotion}
                className="w-full bg-[#3E3F96] hover:bg-[#2D2E7A]"
                disabled={promoting || !promotionData.from_class || !promotionData.to_class || promotionSelectedIds.size === 0}
                data-testid="promote-students-btn"
              >
                {promoting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Promote {promotionSelectedIds.size > 0 ? `${promotionSelectedIds.size} ` : ''}Students
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
